/* ==========================================================================
   MSS TEST PRODUCT TEMPLATE — section-scoped behaviour

   Defines two custom elements used only by sections/mss-test-product.liquid:

     <mss-test-gallery>  main image + thumbnails + prev/next
     <mss-test-product>  variant matrix, VAT pricing, add to basket

   It does not touch global.js, does not observe the DOM, and does not
   rewrite any of Shopify's own .price-item elements. All visible price
   output on this template is owned by this file and by
   snippets/mss-test-price.liquid.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- money */

  function withThousands(intString, separator) {
    return intString.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  }

  /**
   * Formats an integer number of minor units using the shop's own money
   * format string (for example "£{{amount}}").
   */
  function formatMoney(cents, format) {
    var safeFormat = format || '£{{amount}}';
    var negative = cents < 0;
    var value = (Math.abs(cents) / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0];
    var fraction = parts[1];

    var out = safeFormat
      .replace(/\{\{\s*amount\s*\}\}/g, withThousands(whole, ',') + '.' + fraction)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withThousands(whole, ','))
      .replace(
        /\{\{\s*amount_with_comma_separator\s*\}\}/g,
        withThousands(whole, '.') + ',' + fraction
      )
      .replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        withThousands(whole, '.')
      )
      .replace(
        /\{\{\s*amount_with_apostrophe_separator\s*\}\}/g,
        withThousands(whole, "'") + '.' + fraction
      );

    return negative ? '-' + out : out;
  }

  /**
   * Ex-VAT minor units from an inc-VAT amount.
   * Integer maths, and deliberately the same floor behaviour as Liquid's
   * `price | times: 100 | divided_by: 120`, so the server-rendered price and
   * any client update can never disagree by a penny.
   */
  function exVat(incVatCents) {
    return Math.floor((incVatCents * 100) / 120);
  }

  /* -------------------------------------------------------------- gallery */

  if (typeof HTMLElement !== 'function' || !window.customElements) return;

  class MssTestGallery extends HTMLElement {
    connectedCallback() {
        this.stage = this.querySelector('[data-mss-stage]');
        if (!this.stage) return;

        this.items = Array.prototype.slice.call(
          this.querySelectorAll('[data-mss-stage-item]')
        );
        this.thumbs = Array.prototype.slice.call(
          this.querySelectorAll('[data-mss-thumb]')
        );

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => {
            this.activate(thumb.getAttribute('data-media-id'));
          });
        });

        var prev = this.querySelector('[data-mss-stage-prev]');
        var next = this.querySelector('[data-mss-stage-next]');

        if (prev) prev.addEventListener('click', () => this.step(-1));
        if (next) next.addEventListener('click', () => this.step(1));
      }

      activeIndex() {
        for (var i = 0; i < this.items.length; i++) {
          if (this.items[i].classList.contains('is-active')) return i;
        }
        return 0;
      }

      step(direction) {
        if (!this.items.length) return;
        var next = (this.activeIndex() + direction + this.items.length) % this.items.length;
        this.activate(this.items[next].getAttribute('data-media-id'));
      }

      activate(mediaId) {
        if (!mediaId) return;

        this.items.forEach((item) => {
          item.classList.toggle(
            'is-active',
            item.getAttribute('data-media-id') === mediaId
          );
        });

        this.thumbs.forEach((thumb) => {
          var match = thumb.getAttribute('data-media-id') === mediaId;
          thumb.setAttribute('aria-current', match ? 'true' : 'false');
        });
      }
  }

  if (!customElements.get('mss-test-gallery')) {
    customElements.define('mss-test-gallery', MssTestGallery);
  }

  /* --------------------------------------------------------------- config */

  class MssTestProduct extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-mss-variants]');
      if (!data) return;

      try {
        this.variants = JSON.parse(data.textContent);
      } catch (error) {
        this.variants = [];
      }

      this.moneyFormat = this.getAttribute('data-money-format') || '£{{amount}}';
      this.isConsumer = this.getAttribute('data-mss-consumer') === 'true';
      this.threshold = parseInt(this.getAttribute('data-ex-vat-threshold'), 10);
      if (isNaN(this.threshold)) this.threshold = 100000;

      this.productUrl = this.getAttribute('data-product-url') || '';
      this.productTitle = this.getAttribute('data-product-title') || '';
      this.quoteUrl = this.getAttribute('data-quote-url') || '';
      this.leaseUrl = this.getAttribute('data-lease-url') || '';
      this.customFinishValue = (
        this.getAttribute('data-custom-finish-value') || ''
      ).toLowerCase();

      this.inputs = Array.prototype.slice.call(
        this.querySelectorAll('[data-mss-option]')
      );
      this.gallery = this.querySelector('mss-test-gallery');
      this.priceRoot = this.querySelector('[data-mss-price]');
      this.form = this.querySelector('[data-mss-form]');
      this.idInput = this.querySelector('[data-mss-variant-id]');
      this.submit = this.querySelector('[data-mss-submit]');
      this.submitText = this.querySelector('[data-mss-submit-text]');
      this.errorBox = this.querySelector('[data-mss-error]');
      this.finishPanel = this.querySelector('[data-mss-finish-panel]');
      this.finishProperty = this.querySelector('[data-mss-finish-property]');
      this.quoteLinks = Array.prototype.slice.call(
        this.querySelectorAll('[data-mss-quote-link]')
      );
      this.leaseLinks = Array.prototype.slice.call(
        this.querySelectorAll('[data-mss-lease-link]')
      );
      this.jumpButton = this.querySelector('[data-mss-jump-to-basket]');

      this.addEventListener('change', (event) => {
        if (event.target && event.target.matches('[data-mss-option]')) {
          this.onOptionChange();
        }
      });

      if (this.jumpButton) {
        this.jumpButton.addEventListener('click', () => {
          if (!this.submit) return;
          this.submit.scrollIntoView({ block: 'center', behavior: 'smooth' });
          this.submit.focus({ preventScroll: true });
        });
      }

      if (this.form) {
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
      }

      /*
        Initial pass. The price is already correct in the server-rendered
        HTML, so it is deliberately not rewritten here — that is what
        removes the ex VAT to inc VAT flash on first paint.
      */
      this.currentVariant = this.matchVariant(this.selectedValues());
      this.updateAvailability();
      this.updateFinishPanel();
      this.updateLinks();
      this.updateVariantInput();
      this.updateSubmit();
    }

    /* ------------------------------------------------------------ helpers */

    selectedValues() {
      var values = [];
      this.inputs.forEach((input) => {
        if (!input.checked) return;
        var position = parseInt(input.getAttribute('data-option-position'), 10);
        if (isNaN(position)) return;
        values[position - 1] = input.value;
      });
      return values;
    }

    matchVariant(values) {
      for (var i = 0; i < this.variants.length; i++) {
        var variant = this.variants[i];
        var match = true;

        for (var j = 0; j < values.length; j++) {
          if (values[j] === undefined) continue;
          if (variant.options[j] !== values[j]) {
            match = false;
            break;
          }
        }

        if (match) return variant;
      }
      return null;
    }

    /* ------------------------------------------------------------- change */

    onOptionChange() {
      this.currentVariant = this.matchVariant(this.selectedValues());

      /*
        Cart-critical updates run first, so a failure in a cosmetic step can
        never leave the form holding a stale variant id.
      */
      this.updateVariantInput();
      this.updateSubmit();
      this.updateAvailability();
      this.updateFinishPanel();
      this.updateLinks();
      this.updatePrice();
      this.updateMedia();
      this.updateUrl();
      this.clearError();
    }

    updateAvailability() {
      var selected = this.selectedValues();

      this.inputs.forEach((input) => {
        var position = parseInt(input.getAttribute('data-option-position'), 10);
        if (isNaN(position)) return;

        var probe = selected.slice();
        probe[position - 1] = input.value;

        var available = this.variants.some((variant) => {
          if (!variant.available) return false;
          for (var j = 0; j < probe.length; j++) {
            if (probe[j] === undefined) continue;
            if (variant.options[j] !== probe[j]) return false;
          }
          return true;
        });

        var card = input.nextElementSibling;
        if (!card) return;

        card.setAttribute('data-unavailable', available ? 'false' : 'true');

        var flag = card.querySelector('[data-mss-flag]');
        if (flag) flag.textContent = available ? '' : 'Unavailable';
      });
    }

    updatePrice() {
      if (!this.priceRoot || !this.currentVariant) return;

      var inc = Number(this.currentVariant.price);
      var ex = exVat(inc);
      var showEx = !this.isConsumer && ex > this.threshold;

      var primary = this.priceRoot.querySelector('[data-mss-primary-price]');
      var vatLabel = this.priceRoot.querySelector('[data-mss-primary-vat]');
      var incLine = this.priceRoot.querySelector('[data-mss-inc-vat-price]');
      var compare = this.priceRoot.querySelector('[data-mss-compare]');
      var reader = this.priceRoot.querySelector('[data-mss-price-reader]');

      if (primary) {
        primary.textContent = formatMoney(showEx ? ex : inc, this.moneyFormat);
      }

      if (vatLabel) {
        vatLabel.textContent = showEx ? 'ex VAT' : 'inc VAT';
      }

      if (incLine) {
        if (showEx) {
          incLine.textContent = formatMoney(inc, this.moneyFormat) + ' including VAT';
          incLine.hidden = false;
        } else {
          incLine.textContent = '';
          incLine.hidden = true;
        }
      }

      if (compare) {
        var compareInc = Number(this.currentVariant.compare_at_price || 0);
        if (compareInc > inc) {
          var compareShown = showEx ? exVat(compareInc) : compareInc;
          var s = compare.querySelector('s');
          if (s) s.textContent = formatMoney(compareShown, this.moneyFormat);
          compare.hidden = false;
        } else {
          compare.hidden = true;
        }
      }

      if (reader) {
        reader.textContent = showEx
          ? formatMoney(ex, this.moneyFormat) +
            ' excluding VAT, ' +
            formatMoney(inc, this.moneyFormat) +
            ' including VAT'
          : formatMoney(inc, this.moneyFormat) + ' including VAT';
      }
    }

    updateMedia() {
      if (!this.gallery || !this.currentVariant) return;
      if (!this.currentVariant.featured_media) return;
      this.gallery.activate(String(this.currentVariant.featured_media.id));
    }

    updateUrl() {
      if (!this.currentVariant || !this.productUrl) return;
      if (!window.history || !window.history.replaceState) return;

      try {
        window.history.replaceState(
          {},
          '',
          this.productUrl + '?variant=' + this.currentVariant.id
        );
      } catch (error) {
        /* Some embedded contexts refuse replaceState. Not worth failing over. */
      }
    }

    updateVariantInput() {
      if (!this.idInput) return;
      if (this.currentVariant) {
        this.idInput.value = this.currentVariant.id;
        this.idInput.disabled = false;
      } else {
        this.idInput.disabled = true;
      }
    }

    updateSubmit() {
      if (!this.submit || !this.submitText) return;

      if (!this.currentVariant) {
        this.submit.disabled = true;
        this.submitText.textContent = 'Unavailable';
        return;
      }

      if (!this.currentVariant.available) {
        this.submit.disabled = true;
        this.submitText.textContent = 'Sold out';
        return;
      }

      this.submit.disabled = false;
      this.submitText.textContent = this.submit.getAttribute('data-default-label') || 'Add machine to basket';
    }

    isCustomFinishSelected() {
      if (!this.customFinishValue) return false;
      return this.inputs.some(
        (input) =>
          input.checked &&
          String(input.value).toLowerCase() === this.customFinishValue
      );
    }

    updateFinishPanel() {
      var selected = this.isCustomFinishSelected();

      if (this.finishPanel) this.finishPanel.hidden = !selected;

      if (this.finishProperty) {
        this.finishProperty.disabled = !selected;
      }
    }

    configurationLabel() {
      var parts = [];
      this.inputs.forEach((input) => {
        if (!input.checked) return;
        var name = input.getAttribute('data-option-name') || '';
        parts.push(name ? name + ': ' + input.value : input.value);
      });
      return parts.join(', ');
    }

    selectedColour() {
      var found = '';
      var names = ['colour', 'color', 'finish'];
      this.inputs.forEach((input) => {
        if (!input.checked || found) return;
        var name = (input.getAttribute('data-option-name') || '').toLowerCase();
        if (names.indexOf(name) !== -1) found = input.value;
      });
      return found;
    }

    updateLinks() {
      var config = this.configurationLabel();
      var colour = this.selectedColour();

      this.quoteLinks.forEach((link) => {
        var base = link.getAttribute('data-mss-quote-base') || this.quoteUrl;
        if (!base) return;

        var route = link.getAttribute('data-mss-route') || '';
        var url = base + (base.indexOf('?') === -1 ? '?' : '&');
        url += 'product=' + encodeURIComponent(this.productTitle);
        if (config) url += '&configuration=' + encodeURIComponent(config);
        if (colour) url += '&colour=' + encodeURIComponent(colour);
        if (route) url += '&route=' + encodeURIComponent(route);
        link.setAttribute('href', url);
      });

      if (!this.leaseLinks.length || !this.currentVariant || !this.leaseUrl) return;

      var pounds = Math.floor(exVat(Number(this.currentVariant.price)) / 100);

      this.leaseLinks.forEach((link) => {
        var base = this.leaseUrl;
        link.setAttribute(
          'href',
          base + (base.indexOf('?') === -1 ? '?' : '&') + 'finance_amount=' + pounds
        );
      });
    }

    /* ------------------------------------------------------------- submit */

    clearError() {
      if (!this.errorBox) return;
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
    }

    showError(message) {
      if (!this.errorBox) return;
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }

    onSubmit(event) {
      if (!this.form || !this.submit) return;
      if (this.submit.disabled) {
        event.preventDefault();
        return;
      }

      // Without fetch the browser performs a normal form POST to /cart/add.
      if (typeof window.fetch !== 'function') return;

      event.preventDefault();
      this.clearError();
      this.submit.classList.add('is-loading');
      this.submit.setAttribute('aria-busy', 'true');
      this.submit.disabled = true;

      var addUrl =
        (window.routes && window.routes.cart_add_url) || '/cart/add';
      var cartUrl = (window.routes && window.routes.cart_url) || '/cart';

      window
        .fetch(addUrl + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(this.form)
        })
        .then((response) => response.json().then((body) => ({ ok: response.ok, body: body })))
        .then((result) => {
          if (!result.ok) {
            this.showError(
              (result.body && (result.body.description || result.body.message)) ||
                'This machine could not be added to your basket.'
            );
            return;
          }
          window.location.href = cartUrl;
        })
        .catch(() => {
          this.showError(
            'Something went wrong adding this machine to your basket. Please try again.'
          );
        })
        .finally(() => {
          this.submit.classList.remove('is-loading');
          this.submit.removeAttribute('aria-busy');
          this.updateSubmit();
        });
    }
  }

  if (!customElements.get('mss-test-product')) {
    customElements.define('mss-test-product', MssTestProduct);
  }
})();
